const NETWORK_MESSAGE = 'Network Error';

const RESPONSE_HEADERS_FOR_DEBUG = [
  'x-request-id',
  'x-correlation-id',
  'x-trace-id',
  'cf-ray',
  'x-amzn-requestid',
  'server-timing',
  'retry-after',
  'content-type',
];

const SENSITIVE_HEADERS = new Set(['authorization', 'cookie', 'set-cookie', 'x-auth-token', 'x-csrf-token']);

function resolveRequestPath(config) {
  if (!config || typeof config.url !== 'string' || !config.url) {
    return null;
  }

  let path = config.url.split('?')[0];
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return new URL(path).pathname;
  }

  const base = typeof config.baseURL === 'string' ? config.baseURL.replace(/\/$/, '') : '';
  if (path.startsWith('/')) {
    if (base && !path.startsWith(base)) {
      return `${base}${path}`;
    }
    return path;
  }

  return base ? `${base}/${path}` : `/${path}`;
}

const NUMERIC_ROUTE_SEGMENT = /^\d+$/;
const UUID_ROUTE_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function normalizeRouteSegment(segment) {
  if (NUMERIC_ROUTE_SEGMENT.test(segment) || UUID_ROUTE_SEGMENT.test(segment)) {
    return ':id';
  }
  return segment;
}

function normalizeRouteForGrouping(path) {
  return path
    .split('/')
    .map(normalizeRouteSegment)
    .join('/');
}

function routeForErrorName(path) {
  if (!path || typeof path !== 'string') {
    return '/unknown';
  }

  const clean = path.split('?')[0].trim();
  if (!clean) {
    return '/unknown';
  }

  const withSlash = clean.startsWith('/') ? clean : `/${clean}`;
  return normalizeRouteForGrouping(withSlash);
}

function extractBusinessCode(response) {
  const data = response?.data;
  if (data == null) {
    return null;
  }
  if (Array.isArray(data) && data[0] != null && typeof data[0] === 'object' && data[0].code != null) {
    return data[0].code;
  }
  if (typeof data === 'object' && data.code != null) {
    return data.code;
  }
  return null;
}

function buildStoreApiErrorName(statusSuffix, config, response) {
  const route = routeForErrorName(resolveRequestPath(config));
  const bizCode = extractBusinessCode(response);
  const bizPart = bizCode != null ? `_BIZ_${bizCode}` : '';
  return `STORE_API_ERROR_${statusSuffix}${bizPart}_${route}`;
}

function pickResponseHeadersForDebug(headers) {
  if (!headers || typeof headers !== 'object') {
    return undefined;
  }
  const lower = {};
  for (const k of Object.keys(headers)) {
    lower[String(k).toLowerCase()] = headers[k];
  }
  const out = {};
  for (const key of RESPONSE_HEADERS_FOR_DEBUG) {
    const raw = lower[key];
    if (raw == null) continue;
    const v = typeof raw === 'string' ? raw : String(raw);
    out[key] = v.length > 500 ? v.slice(0, 500) + '\u2026' : v;
  }
  return Object.keys(out).length ? out : undefined;
}

function buildRequestHeadersSummary(config) {
  const method = config.method?.toLowerCase();
  const raw = config.headers || {};
  const merged = {};

  for (const source of [raw.common, raw[method], raw]) {
    if (!source || typeof source !== 'object') continue;
    for (const [key, val] of Object.entries(source)) {
      if (typeof val === 'object' && val !== null) continue;
      if (SENSITIVE_HEADERS.has(key.toLowerCase())) continue;
      merged[key] = val;
    }
  }

  return Object.keys(merged).length ? merged : undefined;
}

function buildRequestBodySummary(data) {
  if (data == null) {
    return undefined;
  }
  if (Array.isArray(data)) {
    return { type: 'array', length: data.length };
  }
  if (typeof data !== 'object') {
    return { type: typeof data };
  }

  const summary = {};

  if (data.actionHash) {
    summary.actionHash = data.actionHash;
  }
  if (data.metadata && typeof data.metadata === 'object') {
    if (data.metadata.source) {
      summary.eventSource = data.metadata.source;
    }
    if (data.metadata.type) {
      summary.eventType = data.metadata.type;
    }
  }
  if (data.updatedMap && typeof data.updatedMap === 'object') {
    summary.updatedMapTaskCount = Object.keys(data.updatedMap).length;
  }
  if (data.createdMap && typeof data.createdMap === 'object') {
    summary.createdMapTaskCount = Object.keys(data.createdMap).length;
  }
  if (data.projectIds != null) {
    summary.projectIdsCount = Array.isArray(data.projectIds) ? data.projectIds.length : 1;
  }
  if (data.taskId != null) {
    summary.taskId = data.taskId;
  }
  if (data.ganttId != null) {
    summary.ganttId = data.ganttId;
  }

  if (!Object.keys(summary).length) {
    summary.payloadKeys = Object.keys(data).slice(0, 20);
  }

  return summary;
}

function responseDataForMeta(data) {
  if (data === undefined) {
    return undefined;
  }
  if (Array.isArray(data)) {
    return { items: data };
  }
  if (data !== null && typeof data === 'object') {
    return data;
  }
  return { value: data };
}

function buildNetworkFailureMeta(error) {
  const out = {
    code: error.code,
    message: error.message,
  };

  const req = error.request;
  if (req && typeof req === 'object') {
    if (req.status != null) {
      out.browserRequestStatus = req.status;
    }
    if (req.readyState != null) {
      out.readyState = req.readyState;
    }
    if (req.responseURL) {
      out.responseURL = req.responseURL;
    }
  }

  return out;
}

export function buildAxiosErrorMeta(error, clientKey) {
  const config = error.config;
  const status = error.response?.status;
  const requestPath = resolveRequestPath(config);
  const baseMessage = typeof error?.message === 'string' ? error.message : String(error);

  let errorName;
  if (status != null) {
    errorName = buildStoreApiErrorName(status, config, error.response);
  } else if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
    errorName = buildStoreApiErrorName('TIMEOUT', config, null);
  } else if (error.message === NETWORK_MESSAGE) {
    errorName = buildStoreApiErrorName('NETWORK', config, null);
  } else {
    errorName = buildStoreApiErrorName('UNKNOWN', config, null);
  }

  const capturedMessage = requestPath
    ? `${baseMessage} | ${requestPath}`
    : baseMessage;

  const meta = {};

  if (config) {
    const request = {
      client: clientKey,
      method: config.method,
      url: config.url,
      baseURL: config.baseURL,
    };
    if (requestPath) {
      request.path = requestPath;
    }
    if (config.meta?.requestStartedAt != null) {
      request.requestStartedAt = config.meta.requestStartedAt;
      request.durationMs = new Date().getTime() - config.meta.requestStartedAt;
    }
    if (config.timeout != null) {
      request.timeoutMs = config.timeout;
    }
    if (config.responseType != null) {
      request.responseType = config.responseType;
    }
    if (config.withCredentials === true) {
      request.withCredentials = true;
    }
    if (config.meta?.requestId) {
      request.requestId = config.meta.requestId;
    }
    const headersSummary = buildRequestHeadersSummary(config);
    if (headersSummary) {
      request.headers = headersSummary;
    }
    const bodySummary = buildRequestBodySummary(config.meta?.data);
    if (bodySummary) {
      request.bodySummary = bodySummary;
    }
    meta.APIREQUEST = request;
  }

  if (typeof navigator !== 'undefined' && 'onLine' in navigator) {
    meta.APIRESPONSE = meta.APIRESPONSE || {};
    meta.APIRESPONSE.navigatorOnLine = navigator.onLine;
  }

  if (error.response) {
    const res = error.response;
    const apiResponse = {
      status: res.status,
      statusText: res.statusText,
    };
    const businessCode = extractBusinessCode(res);
    if (businessCode != null) {
      apiResponse.businessCode = businessCode;
    }
    const wrapped = responseDataForMeta(res.data);
    if (wrapped !== undefined) {
      apiResponse.data = wrapped;
    }
    const hdrs = pickResponseHeadersForDebug(res.headers);
    if (hdrs) {
      apiResponse.headers = hdrs;
    }
    meta.APIRESPONSE = { ...meta.APIRESPONSE, ...apiResponse };
  } else {
    meta.APIRESPONSE = {
      ...meta.APIRESPONSE,
      ...buildNetworkFailureMeta(error),
    };
  }

  if (error.isAxiosError === true) {
    meta.APIRESPONSE = { ...meta.APIRESPONSE, isAxiosError: true };
  }

  return { meta, errorName, capturedMessage };
}
